/**
 * EventLinkService
 * Events hub Stage 1B: links from an event to departments, designations, ministries, teams, venues,
 * people and other CCMS records, the coordinator, an event's tags and the reverse lookups.
 * Route: events/api/events (Admin role OR the Event.* right named per method; the event's
 * coordinator may manage its links too)
 *
 * Stage 3: aiSuggest asks AI for link and playbook suggestions (Admin or Event.AI.Use).
 */
import { BaseService } from '../base/BaseService';
import { unwrapEventsResponse as unwrap } from './unwrapEventsResponse';
export class EventLinkService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? EventLinkService.DEFAULT_SERVICE_PATH,
            basePath: EventLinkService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ---- lookups ----
    /** GET api/events/link-lookups (any signed-in user). Link types, roles, origins, statuses and rule lookups. */
    async linkLookups() {
        return unwrap(await this.linkLookupsRaw(), 'Event link lookups not found');
    }
    linkLookupsRaw() {
        return this.http.get(this.buildUrl('link-lookups'));
    }
    // ---- an event's links ----
    /** GET api/events/{id}/links (Event.View or the coordinator). Every link in every status. */
    async getLinks(id) {
        return unwrap(await this.getLinksRaw(id), `Links for event ${id} not found`);
    }
    getLinksRaw(id) {
        return this.http.get(this.buildUrl(`${id}/links`));
    }
    /** POST api/events/{id}/links (Event.Link.Manage or the coordinator). Created as Approved, origin Manual. */
    async addLink(id, request) {
        return unwrap(await this.addLinkRaw(id, request), `Could not link event ${id}`);
    }
    addLinkRaw(id, request) {
        return this.http.post(this.buildUrl(`${id}/links`), request);
    }
    /** POST api/events/{id}/links/{linkId}/approve (Event.Link.Manage or the coordinator). */
    async approve(id, linkId) {
        return unwrap(await this.approveRaw(id, linkId), `Could not approve link ${linkId}`);
    }
    approveRaw(id, linkId) {
        return this.http.post(this.buildUrl(`${id}/links/${linkId}/approve`));
    }
    /** POST api/events/{id}/links/{linkId}/dismiss (Event.Link.Manage or the coordinator). Never suggested again. */
    async dismiss(id, linkId) {
        return unwrap(await this.dismissRaw(id, linkId), `Could not dismiss link ${linkId}`);
    }
    dismissRaw(id, linkId) {
        return this.http.post(this.buildUrl(`${id}/links/${linkId}/dismiss`));
    }
    /** POST api/events/{id}/links/approve-all (Event.Link.Manage or the coordinator). */
    async approveAll(id) {
        return unwrap(await this.approveAllRaw(id), `Could not approve the suggestions for event ${id}`);
    }
    approveAllRaw(id) {
        return this.http.post(this.buildUrl(`${id}/links/approve-all`));
    }
    /** DELETE api/events/{id}/links/{linkId} (Event.Link.Manage or the coordinator). The link becomes Removed. */
    async remove(id, linkId) {
        return unwrap(await this.removeRaw(id, linkId), `Could not remove link ${linkId}`);
    }
    removeRaw(id, linkId) {
        return this.http.delete(this.buildUrl(`${id}/links/${linkId}`));
    }
    /** POST api/events/{id}/links/reviewed (Event.Link.Manage or the coordinator). */
    async markReviewed(id) {
        return unwrap(await this.markReviewedRaw(id), `Could not mark event ${id} as reviewed`);
    }
    markReviewedRaw(id) {
        return this.http.post(this.buildUrl(`${id}/links/reviewed`));
    }
    /**
     * POST api/events/{id}/links/ai-suggest (Admin or Event.AI.Use; being the coordinator is not
     * enough). Adds AI suggestions as Suggested links (origin AI; dismissed and approved links are
     * never added again) and returns them with the suggested playbooks. When AI is switched off
     * (Learning settings or the AIManagement feature flag) `enabled` is false and `message` says why.
     */
    async aiSuggest(id) {
        const res = await this.aiSuggestRaw(id);
        if (res.success === false && (res.data === undefined || res.data === null)) {
            throw new Error(res.message || `Could not get AI suggestions for event ${id}`);
        }
        return unwrap(res, `Could not get AI suggestions for event ${id}`);
    }
    aiSuggestRaw(id) {
        return this.http.post(this.buildUrl(`${id}/links/ai-suggest`));
    }
    /** PUT api/events/{id}/coordinator (Event.Link.Manage). Pass null to clear the coordinator. */
    async setCoordinator(id, userId) {
        return unwrap(await this.setCoordinatorRaw(id, { userId }), `Could not set the coordinator for event ${id}`);
    }
    setCoordinatorRaw(id, request) {
        return this.http.put(this.buildUrl(`${id}/coordinator`), request);
    }
    // ---- an event's tags ----
    /** GET api/events/{id}/tags (Event.View). Source tags and manual tags. */
    async getEventTags(id) {
        const res = await this.getEventTagsRaw(id);
        return res.data ?? [];
    }
    getEventTagsRaw(id) {
        return this.http.get(this.buildUrl(`${id}/tags`));
    }
    /** PUT api/events/{id}/tags (Event.Tag.Manage). Replaces the manual tags; source tags stay. */
    async setEventTags(id, tagIDs) {
        const res = await this.setEventTagsRaw(id, { tagIDs });
        if (res.success === false)
            throw new Error(res.message || `Could not set the tags for event ${id}`);
        return res.data ?? [];
    }
    setEventTagsRaw(id, request) {
        return this.http.put(this.buildUrl(`${id}/tags`), request);
    }
    // ---- reverse lookups ----
    /**
     * GET api/events/linked?typeId&key&from&to (any signed-in user: calendar-visible events only;
     * Event.View: all). For a department (typeId 1) it also returns events linked through its
     * designations (viaDesignation*). Dates are yyyy-MM-dd or ISO.
     */
    async linked(typeId, key, from, to) {
        const res = await this.linkedRaw(typeId, key, from, to);
        return res.data ?? [];
    }
    linkedRaw(typeId, key, from, to) {
        return this.http.get(this.buildUrlWithParams('linked', { typeId, key: String(key), from, to }));
    }
    /** GET api/events/links/by-target?typeId&key (Event.View). Every event linked to one record. */
    async byTarget(typeId, key) {
        const res = await this.byTargetRaw(typeId, key);
        return res.data ?? [];
    }
    byTargetRaw(typeId, key) {
        return this.http.get(this.buildUrlWithParams('links/by-target', { typeId, key: String(key) }));
    }
}
EventLinkService.DEFAULT_SERVICE_PATH = 'events';
EventLinkService.BASE_PATH = 'api/events';
//# sourceMappingURL=EventLinkService.js.map